/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { page as SamplePage } from '../../help/sample_pages/TabsPage';
import { ItemType } from '../../registry/Base.type';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { tabPanelOrientation, tabPanelVariant } from '../../registry/constants';
import { defaultValue, TabPanel, WrapperProps } from './TabPanel';

const TabPanelManager: ComponentManagerFn<'TabPanel', WrapperProps> = (
  platform: Platform,
): ComponentManager<'TabPanel', WrapperProps> => {
  return {
    ...baseComponentManager<'TabPanel', WrapperProps>(platform, 'TabPanel'),
    isChildAllowed: (page, item, childType) =>
      (['TabItem'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['TabItem', 'TabItem', 'TabItem'],
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'hideTitle':
          return itemData.compact;
        case 'disableHoverEffect':
        case 'compact':
        case 'iconSize':
        case 'tabHeaderPadding':
        case 'centered':
        case 'indicatorColor':
        case 'variant':
        case 'orientation':
          return !itemData.hideHeader;
        default:
          return true;
      }
    },
    props: {
      activeTab: {
        type: 'ItemID',
        label: 'Active Tab',
        initialFilter: 'TabItem',
        category: 'Tabs',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const tabs = platform.getChildrenItemsById(
            appUid,
            pageId,
            itemId,
            (c) => c.type === 'TabItem' && c.itemId === value,
          );
          if (!tabs.length) {
            return `Unable to find a tab with this item id '${value}' under this tab panel!`;
          }
          return '';
        },
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        helpText: 'Defines the number of grids the component is going to use.',
        category: 'Spacing',
      },
      centered: {
        label: 'Centered',
        type: 'boolean',
        helpText: 'If checked, the tabs will be centered',
        category: 'Tabs',
      },
      indicatorColor: {
        label: 'Indicator Color',
        type: 'ThemeBGColor',
        helpText: 'The color of tab when selected.',
        category: 'Tabs',
        filterColor: (color) => color !== 'paper',
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: tabPanelVariant,
        category: 'Tabs',
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: tabPanelOrientation,
        category: 'Tabs',
      },
      hideHeader: {
        label: 'Hide Header',
        type: 'boolean',
        helpText:
          'You can use this to hide all the tab headers, to mimic a card panel layout.',
        category: 'Header',
      },
      disableHoverEffect: {
        label: 'Disable Hover Effect',
        type: 'boolean',
        category: 'Header',
      },
      compact: {
        label: 'Icon On Top',
        type: 'boolean',
        category: 'Header',
      },
      hideTitle: {
        label: 'Hide Title',
        type: 'boolean',
        helpText:
          'If checked, the tabs will show icon and title as tooltip. Else title will be shown below icon',
        category: 'Header',
      },
      iconSize: {
        label: 'Icon Size',
        type: 'number',
        min: 16,
        max: 80,
        step: 4,
        category: 'Header',
      },
      tabHeaderPadding: {
        label: 'Tab Header Padding',
        type: 'padding',
        category: 'Header',
      },
      updateURL: {
        label: 'Update URL',
        type: 'boolean',
        helpText:
          'If checked, the URL will be updated with the active tab and will be restored on browser refresh',
        category: 'Tabs',
      },
    },
    name: 'Tab Panel',
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    getWrapperProps: ({ item, platform: _platform, pageId, appUid }) => ({
      setActiveTab: (value: string) => {
        _platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          item.itemId,
          'activeTab',
          value,
        );
      },
    }),
    onChildMoveOut: ({ appUid, pageId, value, child }) => {
      // design prop update
      if (child.type === 'TabItem' && value.props.activeTab === child.itemId) {
        value.props = { ...value.props, activeTab: '' };
      }
      // runtime prop update
      if (
        platform.getRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
        ) === child.itemId
      ) {
        platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
          '',
        );
      }
    },
    onChildMoveIn: ({ appUid, pageId, value, child }) => {
      if (child.type === 'TabItem') {
        platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
          child.itemId,
        );
      }
    },
    icon: { iconName: 'io-tab-panel', iconType: 'cloudio' },
    description: `Tabs make it easy to explore and switch between different views.

Tabs organize and allow navigation between groups of content that are related and at the same level of hierarchy.`,
    helpRenders: [
      {
        title: 'Basic Tabs',
        description: 'Basic Tab Panel with Three Tabs',
        hideProps: true,
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: [],
        item: {
          itemId: 'tabPanel',
          props: {},
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Tabs With Icon',
        description: 'Tabs with both Title & Icon',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['compact'],
        item: {
          itemId: 'tabPanel',
          props: {},
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Tabs Orientation',
        description: `Tabs with Vertical Orientation.

\`\`\`alert
Note: Veritcal Orientation will be in effect only when the available screen width is atleast 1200px.
\`\`\`
`,
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['orientation'],
        height: 240,
        item: {
          itemId: 'tabPanel',
          props: { orientation: 'vertical' },
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Icon Only',
        description: 'Tabs with Icon On Top & Hide Title turned on',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['hideTitle', 'compact', 'hideTitle'],
        height: 240,
        item: {
          itemId: 'tabPanel',
          props: { orientation: 'vertical', compact: true, hideTitle: true },
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Icon Size & Padding',
        description: 'Tabs with larger icon & spacing around it',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['compact', 'iconSize', 'tabHeaderPadding'],
        height: 400,
        item: {
          itemId: 'tabPanel',
          props: {
            orientation: 'vertical',
            compact: true,
            iconSize: 80,
            tabHeaderPadding: { all: 40 },
          },
          children: ['tabItem', 'tabItem1'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Update URL',
        description:
          "With Update URL prop turned on, the Active Tab state is stored in the URL and upon browser refresh the active tab get's restored from the URL state. This allows the URL to be bookmarked along with the active tab state.",
        page: SamplePage,
        exampleId: 'e6',
        visibleProps: ['updateURL'],
        item: {
          itemId: 'tabPanel',
          props: { updateURL: true },
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Hide Header',
        page: SamplePage,
        exampleId: 'e7',
        visibleProps: ['hideHeader'],
        item: {
          itemId: 'tabPanel',
          props: { hideHeader: true },
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default TabPanelManager;
